import React, {useEffect, useState} from 'react'
import {Button, Card, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography} from "@mui/material"
import TablePageSkeleton from "./TablePageSkeleton"
import {Link as RouterLink, useNavigate} from "react-router-dom"
import AddIcon from "@mui/icons-material/Add"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined"
import PlaylistAddOutlinedIcon from "@mui/icons-material/PlaylistAddOutlined"
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined"
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined"
import {useProjectData} from "../../hooks/useProjectData"
import {useIntegrations} from "../../hooks/useIntegrations"
import {RowSearchParams, RowSortProperty, SortDirection} from "../../api/requests/RowRequests";
import ProjectPageHeader from "../layouts/ProjectPageHeader";
import RowTable from "../rows/RowTable";
import TableToolbar from "../rows/TableToolbar";
import CreateRowModal from "../rows/CreateRowModal"
import ImportModal from "../import/ImportModal"
import SynthesizeRunModal from "./SynthesizeRunModal"
import ProjectActionsMenu from "./ProjectsPage/ProjectActionsMenu"
import TransformsDrawer from "../transforms/TransformsDrawer"
import TransformEditModal from "../transforms/TransformEditModal"
import ActiveTasksStrip from "./ActiveTasksStrip"
import {RoleCheck} from "../../library/RoleCheck"
import RowJson from "../../api/json/RowJson"
import TransformJobJson from "../../api/json/TransformJobJson"
import TransformJson from "../../api/json/TransformJson"

export interface OptimisticTransformJob {
    key: number
    job: TransformJobJson
    transformId: string
    transformName: string
}

function TablePage() {
    const navigate = useNavigate()
    const [toolbarParams, setToolbarParams] = useState<Omit<RowSearchParams, 'sort'>>({})
    const [sort, setSort] = useState<RowSearchParams['sort']>({
        by: RowSortProperty.UPDATED,
        direction: SortDirection.DESC,
    })
    const searchParams: RowSearchParams = {...toolbarParams, sort}
    const [reloadCounter, setReloadCounter] = useState(0)
    const [optimisticJob, setOptimisticJob] = useState<OptimisticTransformJob>()

    const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement>()
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const [importModalOpen, setImportModalOpen] = useState(false)
    const [synthesizeModalOpen, setSynthesizeModalOpen] = useState(false)

    const [transformDrawerOpen, setTransformDrawerOpen] = useState(false)
    const [transformEditOpen, setTransformEditOpen] = useState(false)
    const [editingTransformId, setEditingTransformId] = useState<string | undefined>()

    // Latest rows from RowTable; used by ActiveTasksStrip's page-saturation gate.
    const [currentPageRows, setCurrentPageRows] = useState<RowJson[]>([])

    const {id, project, reloadProject} = useProjectData()
    const {openAi} = useIntegrations()

    // Reload the project to make sure we have up-to-date templates and columns
    useEffect(() => reloadProject(), [])

    const canAddRows = project ? project.columns.length > 0 : false
    const canImport = RoleCheck.atLeastManager()
    const canSynthesize = !!project
        && project.columns.length > 0
        && project.templates.length > 0
        && (project.stats.numberOfTrainingRows + project.stats.numberOfValidationRows) >= 3
        && !!openAi
        && RoleCheck.atLeastManager()
    const canTransform = !!project
        && project.columns.length > 0
        && RoleCheck.atLeastManager()

    const showAdd = canImport || canSynthesize || canAddRows

    const closeAddMenu = () => setAddMenuAnchor(undefined)

    const toolbarRightActions = (showAdd || canTransform) ? (
        <>
            {showAdd &&
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon/>}
                    endIcon={<ArrowDropDownIcon/>}
                    onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                >
                    Add Rows
                </Button>
            }
            {canTransform &&
                <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={<AutoAwesomeOutlinedIcon/>}
                    onClick={() => setTransformDrawerOpen(true)}
                >
                    Transforms
                </Button>
            }
        </>
    ) : undefined

    if (!project) {
        return <TablePageSkeleton/>
    }

    const handleCreate = (finished: boolean) => {
        reloadProject()
        setCreateModalOpen(!finished)
        setReloadCounter(reloadCounter + 1)
    }

    const emptyState = (
        <Card variant="outlined" sx={{maxWidth: 520, mx: 'auto', mt: 6, p: 4}}>
            <Stack spacing={2} alignItems="center" textAlign="center">
                <DataObjectOutlinedIcon sx={{fontSize: 48, color: 'text.secondary'}}/>
                <Typography variant="h2">Get started by importing your data</Typography>
                <Typography variant="body2" color="text.secondary">
                    Upload a CSV file to start working with your data.
                </Typography>
                {canImport &&
                    <Button
                        variant="contained"
                        startIcon={<FileUploadOutlinedIcon/>}
                        onClick={() => setImportModalOpen(true)}
                    >
                        Import Data
                    </Button>
                }
                <Typography variant="caption" color="text.secondary">
                    Or <RouterLink to={`/projects/${id}/columns`}>create columns manually</RouterLink> first.
                </Typography>
            </Stack>
        </Card>
    )

    return (
        <>
            <ProjectPageHeader pageTitle="Table">
            </ProjectPageHeader>

            <ActiveTasksStrip
                onDatasetShouldRefresh={() => setReloadCounter(c => c + 1)}
                currentPageRows={currentPageRows}
                optimisticTransformJob={optimisticJob}
                onTransformClick={(transformId) => {
                    setEditingTransformId(transformId)
                    setTransformEditOpen(true)
                }}
            />

            {project.columns.length === 0 ? (
                emptyState
            ) : (
                <Stack direction="column" spacing={1}>
                    <TableToolbar project={project}
                                  onSearchParamChange={setToolbarParams}
                                  rightActions={toolbarRightActions}/>

                    <RowTable project={project}
                                  searchParams={searchParams}
                                  reload={reloadCounter}
                                  onRowsChange={setCurrentPageRows}
                                  onSortChange={setSort}/>
                </Stack>
            )}

            <Menu
                anchorEl={addMenuAnchor}
                open={Boolean(addMenuAnchor)}
                onClose={closeAddMenu}
            >
                {canImport &&
                    <MenuItem onClick={() => {
                        closeAddMenu()
                        setImportModalOpen(true)
                    }}>
                        <ListItemIcon>
                            <FileUploadOutlinedIcon fontSize="small"/>
                        </ListItemIcon>
                        <ListItemText>Upload CSV</ListItemText>
                    </MenuItem>
                }
                {canSynthesize &&
                    <MenuItem onClick={() => {
                        closeAddMenu()
                        setSynthesizeModalOpen(true)
                    }}>
                        <ListItemIcon>
                            <PlaylistAddOutlinedIcon fontSize="small"/>
                        </ListItemIcon>
                        <ListItemText>Synthesize</ListItemText>
                    </MenuItem>
                }
                {canAddRows &&
                    <MenuItem onClick={() => {
                        closeAddMenu()
                        setCreateModalOpen(true)
                    }}>
                        <ListItemIcon>
                            <AddIcon fontSize="small"/>
                        </ListItemIcon>
                        <ListItemText>Manual</ListItemText>
                    </MenuItem>
                }
            </Menu>

            <CreateRowModal
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                keepMounted
                project={project}
                defaultDataset={searchParams?.dataset}
                didCreate={handleCreate}
                allowSaveAndNext
            />

            <ImportModal open={importModalOpen} setOpen={setImportModalOpen}/>

            <SynthesizeRunModal
                project={project}
                open={synthesizeModalOpen}
                onClose={() => setSynthesizeModalOpen(false)}
                onAdded={() => {
                    reloadProject()
                    setReloadCounter(c => c + 1)
                }}
            />

            <TransformsDrawer
                open={transformDrawerOpen}
                onClose={() => setTransformDrawerOpen(false)}
                optimisticTransformJob={optimisticJob}
                onNew={() => {
                    setEditingTransformId(undefined)
                    setTransformEditOpen(true)
                }}
                onEdit={(transformId) => {
                    setEditingTransformId(transformId)
                    setTransformEditOpen(true)
                }}
            />

            <TransformEditModal
                open={transformEditOpen}
                onClose={() => setTransformEditOpen(false)}
                transformId={editingTransformId}
                onJobStarted={(newJob: TransformJobJson, transform: TransformJson) => {
                    reloadProject()
                    setReloadCounter(c => c + 1)
                    setOptimisticJob({
                        key: Date.now(),
                        job: newJob,
                        transformId: transform.id,
                        transformName: transform.name,
                    })
                }}
            />
        </>
    )
}

export default TablePage
